import { ArrowDownToLine, Copy, Loader2, Shield, Wallet } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { usePrivateAxios } from "@/api/use-private-axios";
import { usePaymentTransaction } from "@/api/verify-payments";
import { useViewWallet } from "@/api/wallet-query";
import DiscoveryFooter from "@/components/index/discovery-footer";
import NavbarMinimal from "@/components/index/navbar-minimal";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PaymentProvider } from "@/types/banks";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";

interface ContractData {
	id: string;
	contractCode: string;
	title: string;
	totalContractValue: number;
	deliveryCost: string;
	shippingDate: string;
	contractItem: Array<{
		id: string;
		productQuantity: string;
		totalCost: number;
		vendorDiscountedPrice: number;
		productId: {
			id: string;
			name: string;
			photos: string[];
		};
	}>;
}

interface PaymentPageData {
	courierPickupOfficeId: string;
	orderAmount: number;
}

// User-facing payment method types
type PaymentMethod = "wallet" | "transfer";

interface PaymentMethodOption {
	id: PaymentMethod;
	label: string;
	description: string;
	icon: typeof Wallet;
	// The actual provider sent to the API
	provider: PaymentProvider;
}

const PAYMENT_METHODS: PaymentMethodOption[] = [
	{
		id: "wallet",
		label: "Wallet",
		description: "Pay from your Intasida wallet balance",
		icon: Wallet,
		provider: PaymentProvider.WALLET
	},
	{
		id: "transfer",
		label: "Transfer",
		description: "Pay via bank transfer",
		icon: ArrowDownToLine,
		provider: PaymentProvider.SAFEHAVEN
	}
];

type PageStep = "select" | "confirm" | "transfer-details";

export const Route = createFileRoute("/payment/$contractid")({
	component: PaymentPage
});

function PaymentPage() {
	const { contractid } = Route.useParams();
	const navigate = useNavigate();
	const router = useRouter();
	const privateAxios = usePrivateAxios();
	const tranx = usePaymentTransaction();
	const walletQuery = useViewWallet();

	const [contractData, setContractData] = useState<ContractData | null>(null);
	const [paymentData, setPaymentData] = useState<PaymentPageData | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
	const [step, setStep] = useState<PageStep>("select");
	const [isProcessing, setIsProcessing] = useState(false);

	const walletBalance = Number(walletQuery.data?.data?.withdrawableBalance || 0);

	// Fetch contract data and read payment data from sessionStorage
	useEffect(() => {
		const loadData = async () => {
			try {
				// Read payment data from sessionStorage
				const stored = sessionStorage.getItem("paymentPageData");
				if (!stored) {
					toast.error("Payment data not found. Please start from the order summary.");
					navigate({ to: "/bag" });
					return;
				}

				const parsed: PaymentPageData = JSON.parse(stored);
				setPaymentData(parsed);

				// Fetch contract details
				const response = await privateAxios.get(`/contract/view_contract/${contractid}`);
				const data = response.data?.data;

				if (!data) {
					toast.error("Contract not found");
					navigate({ to: "/bag" });
					return;
				}

				setContractData(data);
			}
			catch (error: any) {
				console.error("Error loading payment data:", error);
				toast.error(error.response?.data?.message || "Failed to load payment details");
				navigate({ to: "/bag" });
			}
			finally {
				setIsLoading(false);
			}
		};

		loadData();
	}, [contractid, privateAxios, navigate]);

	const itemsTotal = contractData?.contractItem?.reduce(
		(sum, item) => sum + item.totalCost,
		0
	) ?? 0;

	// Get the actual provider for the selected method
	const selectedProvider = selectedMethod
		? PAYMENT_METHODS.find(m => m.id === selectedMethod)?.provider ?? null
		: null;

	const selectedMethodInfo = selectedMethod
		? PAYMENT_METHODS.find(m => m.id === selectedMethod)
		: null;

	// Handle initializing payment (step 1 → step 2 or transfer details)
	const handleInitialize = async () => {
		if (!selectedProvider || !paymentData || !contractData)
			return;

		try {
			await tranx.tranxInit.mutateAsync({
				contractId: contractid,
				orderAmount: itemsTotal,
				courierPickupOfficeId: paymentData.courierPickupOfficeId,
				paymentOption: selectedProvider
			});

			if (selectedMethod === "transfer") {
				setStep("transfer-details");
			}
			else {
				setStep("confirm");
			}
		}
		catch (error: any) {
			toast.error(error.response?.data?.message || "Failed to initialize payment");
		}
	};

	// Handle processing payment (step 2 → provider popup / wallet deduction)
	const handlePayNow = async () => {
		if (!selectedProvider || !paymentData || !contractData)
			return;

		setIsProcessing(true);
		try {
			const transactionFee = Number(tranx.tranxInit.data?.order?.transactionFee) || 0;
			const courierFee = Number(tranx.tranxInit.data?.order?.estimatedCourierFee) || 0;
			const totalWithFees = itemsTotal + transactionFee + courierFee;

			await tranx.processPayment({
				contractId: contractid,
				orderAmount: totalWithFees,
				courierPickupOfficeId: paymentData.courierPickupOfficeId,
				paymentOption: selectedProvider
			});

			// Clean up sessionStorage after successful payment initiation
			sessionStorage.removeItem("paymentPageData");
		}
		catch (error: any) {
			toast.error(error.response?.data?.message || "Payment failed");
		}
		finally {
			setIsProcessing(false);
		}
	};

	// Copy text to clipboard helper
	const copyToClipboard = (text: string, label: string) => {
		navigator.clipboard.writeText(text);
		toast.success(`${label} copied to clipboard`);
	};

	if (isLoading) {
		return (
			<div className="bg-gray-50 min-h-screen flex flex-col">
				<NavbarMinimal />
				<div className="flex items-center mb-2">
					<div className="rounded-full p-1" style={{ marginLeft: 12, marginTop: 12 }}>
						<Skeleton className="w-6 h-6 rounded-full" />
					</div>
				</div>
				<div className="flex-1 container mx-auto px-4 py-8 max-w-5xl">
					<Skeleton className="h-8 w-48 mb-6" />
					<div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
						{/* Left column skeleton - Order summary */}
						<div className="space-y-4">
							<div className="bg-white rounded-lg shadow p-6">
								<Skeleton className="h-6 w-36 mb-4" />
								<div className="space-y-4">
									{[1, 2].map(i => (
										<div key={i} className="flex gap-4">
											<Skeleton className="w-16 h-16 rounded" />
											<div className="flex-1 space-y-2">
												<Skeleton className="h-4 w-40" />
												<Skeleton className="h-3 w-20" />
											</div>
											<Skeleton className="h-4 w-16" />
										</div>
									))}
								</div>
								<div className="mt-4 pt-4 border-t space-y-2">
									<div className="flex justify-between">
										<Skeleton className="h-4 w-20" />
										<Skeleton className="h-4 w-24" />
									</div>
									<div className="flex justify-between">
										<Skeleton className="h-4 w-24" />
										<Skeleton className="h-4 w-20" />
									</div>
								</div>
							</div>
						</div>

						{/* Right column skeleton - Payment methods */}
						<div className="space-y-4">
							<div className="bg-white rounded-lg shadow p-6">
								<Skeleton className="h-6 w-40 mb-4" />
								<div className="space-y-3">
									{[1, 2].map(i => (
										<Skeleton key={i} className="h-16 w-full rounded-lg" />
									))}
								</div>
							</div>
							<Skeleton className="h-12 w-full rounded-md" />
						</div>
					</div>
				</div>
				<DiscoveryFooter />
			</div>
		);
	}

	if (!contractData || !paymentData) {
		return (
			<div className="bg-gray-50 min-h-screen flex flex-col">
				<NavbarMinimal />
				<div className="flex-1 flex items-center justify-center">
					<div className="text-center py-20 text-gray-500">Payment data not found</div>
				</div>
				<DiscoveryFooter />
			</div>
		);
	}

	const transactionFee = Number(tranx.tranxInit.data?.order?.transactionFee) || 0;
	const courierFee = Number(tranx.tranxInit.data?.order?.estimatedCourierFee) || 0;
	const grandTotal = itemsTotal + transactionFee + courierFee;

	return (
		<div className="bg-gray-50 min-h-screen flex flex-col">
			<NavbarMinimal />

			{/* Back button */}
			<div className="flex items-center mb-2">
				<button
					type="button"
					aria-label="Go back"
					className="rounded-full p-1 transition-colors duration-150 hover:bg-gray-200"
					style={{ position: "relative", lineHeight: 1, border: "none", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, marginLeft: 12, marginTop: 12 }}
					onClick={() => {
						if (step === "confirm" || step === "transfer-details") {
							setStep("select");
						}
						else {
							router.history.back();
						}
					}}
				>
					<img
						src="/assets/icons/arrow-left.svg"
						alt="Back"
						style={{ width: 24, height: 24, display: "block" }}
					/>
				</button>
			</div>

			<div className="flex-1 container mx-auto px-4 py-8 max-w-5xl pb-16">
				<h1 className="text-2xl font-semibold text-[#2A2A2A] mb-6">
					{step === "select"
						? "Choose payment method"
						: step === "transfer-details"
							? "Transfer details"
							: "Confirm payment"}
				</h1>

				<div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
					{/* Left Column - Order Summary */}
					<div className="space-y-4">
						<div className="bg-white rounded-lg shadow p-6">
							<h2 className="text-lg font-semibold text-[#2A2A2A] mb-1">Order summary</h2>
							<p className="text-sm text-gray-500 mb-4">
								Contract:
								{" "}
								{contractData.contractCode}
							</p>

							{/* Items */}
							<div className="space-y-4">
								{contractData.contractItem?.map(item => (
									<div key={item.id} className="flex gap-4">
										<img
											src={item.productId?.photos?.[0] || "/placeholder.png"}
											alt={item.productId?.name || "Product"}
											className="w-16 h-16 object-cover rounded"
										/>
										<div className="flex-1 min-w-0">
											<h3 className="text-sm font-medium text-[#2A2A2A] truncate">
												{item.productId?.name || "Product"}
											</h3>
											<p className="text-xs text-gray-500 mt-1">
												Qty:
												{" "}
												{item.productQuantity}
											</p>
										</div>
										<span className="text-sm font-semibold text-[#2A2A2A] whitespace-nowrap">
											₦
											{item.totalCost.toLocaleString()}
										</span>
									</div>
								))}
							</div>
						</div>

						{/* Payment breakdown — shows after payment is initialized */}
						{(step === "confirm" || step === "transfer-details") && (
							<div className="bg-white rounded-lg shadow p-6">
								<h2 className="text-lg font-semibold text-[#2A2A2A] mb-3">Payment breakdown</h2>
								<div className="space-y-2 text-sm">
									<div className="flex justify-between">
										<span className="text-gray-600">Order amount</span>
										<span className="font-medium text-[#2A2A2A]">
											₦
											{itemsTotal.toLocaleString()}
										</span>
									</div>
									<div className="flex justify-between">
										<span className="text-gray-600">Delivery fee</span>
										<span className="font-medium text-[#2A2A2A]">
											{courierFee > 0
												? `₦${courierFee.toLocaleString()}`
												: "₦0"}
										</span>
									</div>
									<div className="flex justify-between">
										<span className="text-gray-600">Transaction fee</span>
										<span className="font-medium text-[#2A2A2A]">
											{transactionFee > 0
												? `₦${transactionFee.toLocaleString()}`
												: "₦0"}
										</span>
									</div>
									<div className="flex justify-between pt-3 border-t border-gray-200">
										<span className="font-bold text-[#2A2A2A]">Total to pay</span>
										<span className="font-bold text-xl text-[#3F2EC8]">
											₦
											{grandTotal.toLocaleString()}
										</span>
									</div>
								</div>
							</div>
						)}

						{/* Security note */}
						<div className="flex items-center gap-2 bg-[#E2E1EB] rounded-lg px-4 py-3">
							<Shield className="w-4 h-4 text-[#3F2EC8] flex-shrink-0" />
							<p className="text-xs text-[#2A2A2A]">
								Your payment is protected by Intasida's buyer guarantee. Funds are held securely until you confirm delivery.
							</p>
						</div>
					</div>

					{/* Right Column - Payment Method Selection / Transfer Details / Confirmation */}
					<div className="space-y-4">
						{step === "select" && (
							<>
								<div className="bg-white rounded-lg shadow p-6">
									<h2 className="text-lg font-semibold text-[#2A2A2A] mb-4">Payment method</h2>
									<div className="space-y-3">
										{PAYMENT_METHODS.map((method) => {
											const Icon = method.icon;
											const isWallet = method.id === "wallet";
											const isSelected = selectedMethod === method.id;
											const insufficientBalance = isWallet && walletBalance < itemsTotal;

											return (
												<button
													key={method.id}
													type="button"
													className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all duration-150 text-left ${
														isSelected
															? "border-[#3F2EC8] bg-[#F5F3FF]"
															: "border-gray-200 hover:border-gray-300 bg-white"
													} ${insufficientBalance ? "opacity-60" : ""}`}
													onClick={() => !insufficientBalance && setSelectedMethod(method.id)}
													disabled={insufficientBalance}
												>
													<div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
														isSelected ? "bg-[#3F2EC8] text-white" : "bg-gray-100 text-gray-500"
													}`}
													>
														<Icon className="w-5 h-5" />
													</div>
													<div className="flex-1 min-w-0">
														<div className="flex items-center gap-2">
															<span className="text-sm font-semibold text-[#2A2A2A]">{method.label}</span>
															{isWallet && (
																<span className="text-xs text-gray-500">
																	(₦
																	{walletBalance.toLocaleString()}
																	)
																</span>
															)}
														</div>
														<p className="text-xs text-gray-500 mt-0.5">
															{insufficientBalance ? "Insufficient balance" : method.description}
														</p>
													</div>
													<div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
														isSelected ? "border-[#3F2EC8]" : "border-gray-300"
													}`}
													>
														{isSelected && <div className="w-2.5 h-2.5 rounded-full bg-[#3F2EC8]" />}
													</div>
												</button>
											);
										})}
									</div>
								</div>

								<Button
									onClick={handleInitialize}
									disabled={!selectedMethod || tranx.tranxInit.isPending}
									className="w-full bg-[#3F2EC8] hover:bg-[#2F1EA8] text-white py-3 text-base"
								>
									{tranx.tranxInit.isPending
										? (
												<>
													<Loader2 className="w-4 h-4 animate-spin mr-2" />
													Initializing...
												</>
											)
										: "Continue"}
								</Button>
							</>
						)}

						{step === "transfer-details" && (
							<>
								<div className="bg-white rounded-lg shadow p-6">
									<h2 className="text-lg font-semibold text-[#2A2A2A] mb-2">Bank transfer details</h2>
									<p className="text-sm text-gray-500 mb-5">
										Transfer the exact amount below to complete your payment.
									</p>

									{/* Transfer details card */}
									<div className="bg-[#F5F3FF] rounded-lg p-5 space-y-4">
										{/* Bank name */}
										<div>
											<p className="text-xs text-gray-500 mb-1">Bank</p>
											<p className="text-sm font-semibold text-[#2A2A2A]">SafeHaven Microfinance Bank</p>
										</div>
										{/* Account name */}
										<div>
											<p className="text-xs text-gray-500 mb-1">Account Name</p>
											<p className="text-sm font-semibold text-[#2A2A2A]">
												{tranx.tranxInit.data?.paymentConfig?.settlementAccount?.accountName || "Intasida Escrow"}
											</p>
										</div>
										{/* Account number */}
										<div>
											<p className="text-xs text-gray-500 mb-1">Account Number</p>
											<div className="flex items-center justify-between">
												<p className="text-lg font-bold text-[#2A2A2A] tracking-wider">
													{tranx.tranxInit.data?.paymentConfig?.settlementAccount?.accountNumber || "—"}
												</p>
												<button
													type="button"
													className="flex items-center gap-1.5 text-xs text-[#3F2EC8] hover:text-[#2F1EA8] font-medium transition-colors"
													onClick={() => copyToClipboard(
														tranx.tranxInit.data?.paymentConfig?.settlementAccount?.accountNumber || "",
														"Account number"
													)}
												>
													<Copy className="w-3.5 h-3.5" />
													Copy
												</button>
											</div>
										</div>

										{/* Amount */}
										<div>
											<p className="text-xs text-gray-500 mb-1">Amount to transfer</p>
											<div className="flex items-center justify-between">
												<p className="text-lg font-bold text-[#3F2EC8]">
													₦
													{grandTotal.toLocaleString()}
												</p>
												<button
													type="button"
													className="flex items-center gap-1.5 text-xs text-[#3F2EC8] hover:text-[#2F1EA8] font-medium transition-colors"
													onClick={() => copyToClipboard(grandTotal.toString(), "Amount")}
												>
													<Copy className="w-3.5 h-3.5" />
													Copy
												</button>
											</div>
										</div>

										{/* Reference */}
										{tranx.tranxInit.data?.order?.paymentRef && (
											<div>
												<p className="text-xs text-gray-500 mb-1">Payment Reference</p>
												<div className="flex items-center justify-between">
													<p className="text-sm font-mono font-semibold text-[#2A2A2A]">
														{tranx.tranxInit.data.order.paymentRef}
													</p>
													<button
														type="button"
														className="flex items-center gap-1.5 text-xs text-[#3F2EC8] hover:text-[#2F1EA8] font-medium transition-colors"
														onClick={() => copyToClipboard(
															tranx.tranxInit.data?.order?.paymentRef || "",
															"Reference"
														)}
													>
														<Copy className="w-3.5 h-3.5" />
														Copy
													</button>
												</div>
											</div>
										)}
									</div>

									{/* Instructions */}
									<div className="mt-4 text-xs text-gray-500 space-y-1">
										<p>
											• Transfer the
											<strong>exact amount</strong>
											{" "}
											to avoid delays.
										</p>
										<p>• Payment will be confirmed automatically once received.</p>
										<p>• Funds are held securely until you confirm delivery.</p>
									</div>
								</div>

								<Button
									onClick={handlePayNow}
									disabled={isProcessing || tranx.verifyPayment.isPending}
									className="w-full bg-[#3F2EC8] hover:bg-[#2F1EA8] text-white py-3 text-base"
								>
									{isProcessing || tranx.verifyPayment.isPending
										? (
												<>
													<Loader2 className="w-4 h-4 animate-spin mr-2" />
													Verifying payment...
												</>
											)
										: "I've made the transfer"}
								</Button>

								<button
									type="button"
									className="w-full text-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
									onClick={() => setStep("select")}
								>
									Choose a different payment method
								</button>
							</>
						)}

						{step === "confirm" && (
							<>
								<div className="bg-white rounded-lg shadow p-6">
									{/* Selected method */}
									<div className="flex items-center gap-3 p-3 bg-[#F5F3FF] rounded-lg mb-4">
										{selectedMethodInfo && (() => {
											const Icon = selectedMethodInfo.icon;
											return <Icon className="w-5 h-5 text-[#3F2EC8]" />;
										})()}
										<div>
											<span className="text-sm font-semibold text-[#2A2A2A]">
												Paying with
												{" "}
												{selectedMethodInfo?.label}
											</span>
											<button
												type="button"
												className="text-xs text-[#3F2EC8] ml-2 underline"
												onClick={() => setStep("select")}
											>
												Change
											</button>
										</div>
									</div>
								</div>

								{/* Wallet warning if applicable */}
								{selectedMethod === "wallet" && walletBalance < grandTotal && (
									<div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
										<p className="text-xs text-red-600">
											Insufficient wallet balance. You need ₦
											{(grandTotal - walletBalance).toLocaleString()}
											{" "}
											more. Please choose a different payment method.
										</p>
									</div>
								)}

								<Button
									onClick={handlePayNow}
									disabled={
										isProcessing
										|| tranx.verifyPayment.isPending
										|| (selectedMethod === "wallet" && walletBalance < grandTotal)
									}
									className="w-full bg-[#3F2EC8] hover:bg-[#2F1EA8] text-white py-3 text-base"
								>
									{isProcessing || tranx.verifyPayment.isPending
										? (
												<>
													<Loader2 className="w-4 h-4 animate-spin mr-2" />
													Processing payment...
												</>
											)
										: (
												<>
													Pay ₦
													{grandTotal.toLocaleString()}
												</>
											)}
								</Button>

								<p className="text-center text-xs text-gray-400 mt-2">
									By proceeding, you agree to Intasida's terms of service.
								</p>
							</>
						)}
					</div>
				</div>
			</div>

			<DiscoveryFooter />
		</div>
	);
}
